import React, { useEffect, useState } from 'react';
import { getInquiry } from '../../api/inquiries';
import AdminButton from '../../components/admin/AdminButton';

const InquiryDetailModal = ({ inquiryId, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (inquiryId) {
            fetchDetail();
        }
    }, [inquiryId]);

    const fetchDetail = async () => {
        try {
            const data = await getInquiry(inquiryId);
            setDetail(data);
        } catch (error) {
            console.error(error);
            alert('상세 정보를 불러올 수 없습니다.');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'ESTIMATE': return '견적문의';
            case 'CONTACT': return '일반문의';
            case 'DEMO': return '데모신청';
            case 'SUPPORT': return '기술지원';
            default: return type;
        }
    };

    const getCopyText = (d) => {
        return `[문의 상세]\n` +
            `- 문의일시: ${new Date(d.createdAt).toLocaleString()}\n` +
            `- 유형: ${getTypeLabel(d.type)}\n` +
            `- 고객명: ${d.customerName} (${d.companyName})\n` +
            `- 연락처: ${d.contactInfo}\n` +
            `- 이메일: ${d.email}\n` +
            `- 상태: ${d.status}\n` +
            `\n[내용]\n${d.message}`;
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('내용이 클립보드에 복사되었습니다.');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    if (!inquiryId) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-5 border-b border-gray-100 shrink-0">
                    <h3 className="text-lg font-bold text-gray-800 m-0">문의 상세 정보 <span className="text-gray-400 text-base font-normal">(#{inquiryId})</span></h3>
                    <button className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent border-none cursor-pointer outline-none transition-colors" onClick={onClose}>&times;</button>
                </div>

                <div className="overflow-y-auto p-5 space-y-6 flex-1 bg-white">
                    {loading ? (
                        <div className="text-center py-10 text-gray-400 font-bold animate-pulse">데이터 로딩 중...</div>
                    ) : detail ? (
                        <>
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 space-y-3">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">문의일시</span><span className="flex-1 text-gray-800 font-semibold text-sm">{new Date(detail.createdAt).toLocaleString()}</span></div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">유형</span><span className="flex-1 text-gray-800 font-semibold text-sm"><span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-xs">{getTypeLabel(detail.type)}</span></span></div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">고객명</span><span className="flex-1 text-gray-800 font-semibold text-sm">{detail.customerName} ({detail.companyName})</span></div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">연락처</span><span className="flex-1 text-gray-800 font-semibold text-sm">{detail.contactInfo}</span></div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">이메일</span><span className="flex-1 text-gray-800 font-semibold text-sm">{detail.email}</span></div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"><span className="w-24 shrink-0 font-bold text-gray-500 text-sm">상태</span><span className="flex-1 text-gray-800 font-semibold text-sm">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                        detail.status === 'UNREAD' ? 'bg-red-100 text-red-700' :
                                        detail.status === 'RESPONDED' ? 'bg-blue-100 text-blue-700' :
                                        detail.status === 'DONE' ? 'bg-gray-200 text-gray-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>{detail.status}</span>
                                </span></div>
                                <div className="flex flex-col sm:flex-row items-start gap-1 sm:gap-4 mt-2">
                                    <span className="w-24 shrink-0 font-bold text-gray-500 text-sm pt-1">내용</span>
                                    <div className="flex-1 text-sm">
                                        <div className="whitespace-pre-wrap text-gray-800 leading-relaxed bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-3">
                                            {detail.message}
                                        </div>
                                        <AdminButton variant="secondary" size="sm" onClick={() => handleCopy(getCopyText(detail))}>
                                            📋 텍스트로 정보 전체 복사
                                        </AdminButton>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-base font-bold text-gray-800 m-0 border-b border-gray-100 pb-2">히스토리 (최근 활동)</h4>
                                <div className="space-y-3">
                                    {detail.history && detail.history.length > 0 ? (
                                        detail.history.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((h) => (
                                            <div key={h.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-sm">
                                                <div className="flex justify-between items-center text-xs text-gray-400 mb-2 font-medium">
                                                    <span>{new Date(h.createdAt).toLocaleString()}</span>
                                                    <span className="bg-gray-50 px-2 py-0.5 rounded text-gray-500">🧑‍💻 {h.executor}</span>
                                                </div>
                                                <div className="text-gray-700 leading-relaxed">
                                                    <strong className="text-primary-600 mr-1 pb-1">[{h.action}]</strong> 
                                                    {h.detail}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-6 text-gray-400 text-center font-bold bg-gray-50 rounded-xl border border-dashed border-gray-200">활동 기록이 없습니다.</div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-10 text-gray-400 font-bold">요청한 데이터가 삭제되었거나 존재하지 않습니다.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InquiryDetailModal;
